import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import VueFinalModal from 'vue-final-modal';
import VCalendar from 'v-calendar';
import Quasar from 'quasar/src/vue-plugin.js';;
import quasarUserOptions from './quasar-user-options';
import axios from 'axios';
import SocialSharing from 'vue-social-sharing';
import VueTour from 'v3-tour';
import authService from './services/auth.service';
import requestState from './helpers/requestState';
import store from './store';
import Cookies from "js-cookie";

require('v3-tour/dist/vue-tour.css');

const app = createApp(App)
  .use(router)
  .use(Quasar, quasarUserOptions)
  .use(VueFinalModal())
  .use(VCalendar, {})
  .use(SocialSharing)
  .use(VueTour);

app.config.globalProperties.axios = axios;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ignore refreshing token if the first try fails
    if (originalRequest.url.includes("/auth/refresh")) {
      if (!requestState.isLogoutInProgress) {
        requestState.isLogoutInProgress = true;
        const returnUrl = originalRequest.url.includes("/api")
          ? ""
          : originalRequest.url;
        router.push({
          name: "Login",
          query: { returnUrl },
        });
      }
      
      return Promise.reject(error);
    }

    // if authentication fails, try to get new access token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (requestState.isLogoutInProgress) {
        return Promise.reject(error);
      }

      try {
        if (requestState.isRateLimitActive) {
          return Promise.reject(error);
        }

        if (!requestState.isRefreshing) {
          requestState.isRefreshing = true;
          requestState.refreshPromise = authService
            .refreshToken()
            .then(() => authService.getAuthUserData())
            .finally(() => {
              requestState.isRefreshing = false;
            });
        }
        // wait for the refreshPromise
        await requestState.refreshPromise;
        // Retry the originalRequest
        return axios(originalRequest);
      }
      catch (refreshTokenError) { // if server returns 429 (too many requests), pause all requests for 30 seconds
        if (refreshTokenError.response?.status === 429) {
          requestState.isRateLimitActive = true;

          store.commit("auth/enableModalError");
          store.commit(
            "auth/updateModalError",
            refreshTokenError.response.data
          );

          await authService.logout(false);
          requestState.isRefreshing = false;
          requestState.isLogoutInProgress = false;
          requestState.refreshPromise = null;
          
          await router.push({
            name: "Login",
          });

          setTimeout(() => {
            requestState.isRateLimitActive = false;
          }, 60000);
          return Promise.reject(refreshTokenError);
        }
        else if (refreshTokenError.response?.status === 403) {
        // Refresh token is not valid, clear authUser and don't call logout on the server side
        await authService.logout(false);
        requestState.isRefreshing = false;
        requestState.isLogoutInProgress = false;
        requestState.refreshPromise = null;
        const returnUrl = originalRequest.url.includes('/api') ? '' : originalRequest.url;
        router.push({
          name: "Login",
          query: { returnUrl },
        });

        return Promise.reject(refreshTokenError);
        } else {
          await authService.logout(false);
          requestState.isRefreshing = false;
          requestState.isLogoutInProgress = false;
          requestState.refreshPromise = null;
          return Promise.reject(refreshTokenError);
        }
      }
    }

    // if authorization fails, log out user
    if (error.response?.status === 403) {
      requestState.isRefreshing = false;
      requestState.isLogoutInProgress = false;
      requestState.refreshPromise = null;
      const returnUrl = originalRequest.url.includes('/api') ? '' : originalRequest.url;
      authService.logout(true);
      router.push({
        name: "Login",
        query: { returnUrl },
      });
    }
    return Promise.reject(error);
  }
);

axios.interceptors.request.use(function (config) {
  let xsrfCookieKey = localStorage.getItem('xsrf-cookie-key');
  if (xsrfCookieKey) {
    const xsrfToken = Cookies.get(xsrfCookieKey);
    if (xsrfToken && ["post", "put", "delete"].includes(config.method)) {
      config.headers["X-XSRF-TOKEN"] = xsrfToken;
    }
  }
  return config;
});

router.isReady().then(() => {
  app.mount('#app');
});
